import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { submitRiskReport, getRiskReports } from '../services/api';
import { 
  Shield, ArrowLeft, AlertTriangle, Plus, X, Clock, 
  CheckCircle, AlertCircle, FileText, MapPin
} from 'lucide-react';

const RISK_CATEGORIES = [
  'Safety & Security',
  'Cybersecurity',
  'Facilities',
  'Financial',
  'Legal & Compliance',
  'Operational',
  'Reputational',
  'Personnel',
  'Academic',
  'Transportation',
  'Health & Wellness',
  'Other'
];

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low', color: '#059669', bg: 'bg-green-50' },
  { value: 'medium', label: 'Medium', color: '#D97706', bg: 'bg-amber-50' },
  { value: 'high', label: 'High', color: '#DC2626', bg: 'bg-red-50' },
  { value: 'critical', label: 'Critical', color: '#7C2D12', bg: 'bg-red-100' },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    severity: '',
    location: ''
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await getRiskReports();
      setReports(response.data.reports);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await submitRiskReport(formData);
      setShowForm(false);
      setFormData({ title: '', description: '', category: '', severity: '', location: '' });
      fetchReports();
    } catch (error) {
      console.error('Failed to submit report:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getSeverityInfo = (severity) => SEVERITY_LEVELS.find(s => s.value === severity) || SEVERITY_LEVELS[0];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="w-5 h-5 text-risk-safe" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-risk-warning" />;
      default:
        return <AlertCircle className="w-5 h-5 text-accent-blue" />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-surface border-b border-border px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-md transition-all">
              <ArrowLeft className="w-5 h-5 text-text-muted" />
            </Link>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              <span className="font-outfit text-xl font-bold text-primary">Risk Reports</span>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-risk-warning text-white px-4 py-2 rounded-md font-medium hover:bg-amber-600 transition-all"
            data-testid="new-report-btn"
          >
            <Plus className="w-5 h-5" />
            Report Risk
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-surface border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">Total</p>
            <p className="text-2xl font-outfit font-bold text-primary">{reports.length}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">Pending</p>
            <p className="text-2xl font-outfit font-bold text-accent-blue">
              {reports.filter(r => r.status === 'pending').length}
            </p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">In Progress</p>
            <p className="text-2xl font-outfit font-bold text-risk-warning">
              {reports.filter(r => r.status === 'in_progress').length}
            </p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">Resolved</p>
            <p className="text-2xl font-outfit font-bold text-risk-safe">
              {reports.filter(r => r.status === 'resolved').length}
            </p>
          </div>
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="bg-surface border border-border rounded-lg p-8 text-center">
            <div className="animate-pulse text-text-muted">Loading reports...</div>
          </div>
        ) : reports.length === 0 ? (
          <div className="bg-surface border border-border rounded-lg p-12 text-center" data-testid="no-reports">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="font-outfit text-xl font-semibold text-primary mb-2">No Reports Yet</h3>
            <p className="text-text-muted mb-6">Be proactive! Report potential risks to keep your district safe.</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 bg-risk-warning text-white px-6 py-3 rounded-md font-medium hover:bg-amber-600 transition-all"
            >
              <Plus className="w-5 h-5" />
              Report Your First Risk
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report, index) => {
              const severityInfo = getSeverityInfo(report.severity);
              return (
                <div 
                  key={index}
                  className="bg-surface border border-border rounded-lg p-6 hover:border-slate-300 transition-all animate-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`report-${index}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span 
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${severityInfo.bg}`}
                          style={{ color: severityInfo.color }}
                        >
                          {severityInfo.label}
                        </span>
                        <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-semibold text-text-muted">
                          {report.category}
                        </span>
                      </div>
                      <h3 className="font-outfit text-lg font-semibold text-primary mb-2">{report.title}</h3>
                      <p className="text-text-muted mb-4">{report.description}</p>
                      <div className="flex items-center gap-4 text-sm text-text-muted">
                        {report.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {report.location}
                          </span>
                        )}
                        <span>Reported by {report.reporter_name}</span>
                        <span>{new Date(report.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(report.status)}
                      <span className="text-sm font-medium capitalize text-primary">
                        {report.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Report Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in" data-testid="report-form-modal">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-risk-warning" />
                <h2 className="font-outfit text-xl font-semibold text-primary">Report a Risk</h2>
              </div>
              <button 
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-slate-100 rounded-md transition-all"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  placeholder="Brief description of the risk"
                  required
                  data-testid="report-title"
                />
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  required
                  data-testid="report-category"
                >
                  <option value="">Select a category</option>
                  {RISK_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Severity *
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {SEVERITY_LEVELS.map(level => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, severity: level.value })}
                      className={`py-2 px-3 rounded-md border-2 text-sm font-medium transition-all ${
                        formData.severity === level.value
                          ? 'border-current'
                          : 'border-border hover:border-slate-300'
                      }`}
                      style={{ 
                        color: formData.severity === level.value ? level.color : '#64748B',
                        backgroundColor: formData.severity === level.value ? `${level.color}10` : 'transparent'
                      }}
                      data-testid={`severity-${level.value}`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  placeholder="Building, room, or area (optional)"
                  data-testid="report-location"
                />
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all resize-none"
                  rows={4}
                  placeholder="Provide details about the risk, including any immediate concerns or observations"
                  required
                  data-testid="report-description"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-3 border border-border rounded-md font-medium text-primary hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !formData.title || !formData.category || !formData.severity || !formData.description}
                  className="flex-1 py-3 bg-risk-warning text-white rounded-md font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="submit-report"
                >
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
